import React, { useState, useEffect, useRef } from 'react';
import { Trophy, RotateCcw, Pencil, Info, Share2, Camera, X, Download, Lock, Unlock } from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import { 
  TOTAL_TEAMS, 
  TOTAL_ROUNDS, 
  PRESET_TEAMS,
  CURRENT_PRESET_LABEL,
  ROUND_COLORS,
  MIN_POWER,
  MAX_GIFTS,
  MAX_TEAM_NAME_LENGTH,
} from './constants';
import { 
  formatPowerDisplay,
  createDuels,
  determineWinner,
  calculateNextRanking,
  calculateNextRankingWithSorting,
  updateTeamInAllRounds,
  serializeGameState,
  deserializeGameState
} from './utils';

// eslint-disable-next-line no-unused-vars
const TeamRankingGame = () => {
  const [rounds, setRounds] = useState([[], [], [], []]);
  const [winners, setWinners] = useState([{}, {}, {}, {}]);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingPowerValue, setEditingPowerValue] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const carouselRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const stateLoadAttemptedRef = useRef(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [capturedImageUrl, setCapturedImageUrl] = useState(null);
  const hiddenTableRef = useRef(null);
  const [lockedRounds, setLockedRounds] = useState([false, false, false, false]); // All rounds unlocked by default

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (stateLoadAttemptedRef.current) {
      return;
    }
    stateLoadAttemptedRef.current = true;

    // Check for game state in URL
    const urlParams = new URLSearchParams(window.location.search);
    const stateParam = urlParams.get('state');
    
    let stateLoaded = false;
    
    if (stateParam) {
      try {
        const gameState = deserializeGameState(stateParam);
        setRounds(gameState.rounds);
        setWinners(gameState.winners);
        if (gameState.selectedTeamId !== null) {
          setSelectedTeamId(gameState.selectedTeamId);
        }
        // Load locked rounds state (default to [false, false, false, false] if not present)
        if (gameState.lockedRounds && Array.isArray(gameState.lockedRounds)) {
          setLockedRounds(gameState.lockedRounds);
        }
        // Clear URL parameter for cleaner URLs
        window.history.replaceState({}, '', window.location.pathname);
        stateLoaded = true;
      } catch (error) {
        console.error('Failed to load game state from URL:', error);
        // Fall through to default initialization
      }
    }
    
    // Only initialize default teams if no state was loaded
    if (!stateLoaded) {
      const initialTeams = Array.from({ length: TOTAL_TEAMS }, (_, i) => ({
        id: i,
        name: `Team ${i + 1}`,
        power: 0,
        gifts: 0
      }));
      setRounds([initialTeams, [], [], []]);
    }
  }, []);

  // Handle horizontal scrolling with mouse wheel
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const { scrollLeft, scrollWidth, clientWidth } = carousel;
      const maxScroll = scrollWidth - clientWidth;

      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= maxScroll - 1;

      const scrollingDown = e.deltaY > 0;
      const scrollingUp = e.deltaY < 0;

      if ((scrollingDown && !isAtEnd) || (scrollingUp && !isAtStart)) {
        e.preventDefault();
        
        carousel.style.scrollSnapType = 'none';
        
        // Move the scroll
        carousel.scrollBy({
          left: e.deltaY * 1.5,
          behavior: 'auto'
        });

        // UPDATE PROGRESS BAR HERE
        const newProgress = (carousel.scrollLeft / maxScroll) * 100;
        setScrollProgress(newProgress);

        clearTimeout(carousel.snapTimeout);
        carousel.snapTimeout = setTimeout(() => {
          carousel.style.scrollSnapType = 'x proximity';
          // Final check to sync bar after snapping
          setScrollProgress((carousel.scrollLeft / maxScroll) * 100);
        }, 150);
      }
    };

    carousel.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      carousel.removeEventListener('wheel', handleWheel);
      clearTimeout(carousel.snapTimeout);
    };
  }, []);

  const updateTeamName = (id, newName) => {
    setRounds(prev => updateTeamInAllRounds(prev, id, 'name', newName));
  };

  const updateTeamPower = (id, newPower, isEditing = false) => {
    if (isEditing) {
      // Just store the raw input value while editing
      setEditingPowerValue(newPower);
    } else {
      // Commit the value when done editing
      const power = Math.max(MIN_POWER, parseFloat(newPower) || 0);
      setRounds(prev => updateTeamInAllRounds(prev, id, 'power', power));
      setEditingPowerValue('');
    }
  };

  const updateTeamGifts = (id, newGifts) => {
    const gifts = Math.max(0, Math.min(MAX_GIFTS, parseInt(newGifts) || 0));
    setRounds(prev => updateTeamInAllRounds(prev, id, 'gifts', gifts));
  };

  const selectWinner = (roundIndex, duelIndex, teamId) => {
    const newWinners = { ...winners[roundIndex], [duelIndex]: teamId };
    const updatedWinners = [...winners];
    updatedWinners[roundIndex] = newWinners;
    setWinners(updatedWinners);

    if (roundIndex < 3) {
      calculateNextRound(roundIndex, updatedWinners);
    }
  };

  const calculateNextRound = (currentRound, updatedWinners) => {
    const currentRanking = rounds[currentRound];
    const duels = createDuels(currentRanking);
    const currentWinners = updatedWinners[currentRound];
    
    // For rounds 2-4 (roundIndex 1, 2, 3), use sorting logic
    // Round 1 (roundIndex 0) doesn't need sorting as it's the initial round
    if (currentRound >= 1) {
      const previousRoundRanking = rounds[currentRound];
      const combinedRanking = calculateNextRankingWithSorting(
        duels,
        currentWinners,
        previousRoundRanking,
        rounds,
        updatedWinners,
        currentRound,
        true // keepNulls
      );
      
      const newRounds = [...rounds];
      newRounds[currentRound + 1] = combinedRanking;
      setRounds(newRounds);
    } else {
      // For round 1, use the old method (no sorting needed)
      const combinedRanking = calculateNextRanking(duels, currentWinners, true);
      const newRounds = [...rounds];
      newRounds[currentRound + 1] = combinedRanking;
      setRounds(newRounds);
    }
  };

  const loadPresetNames = () => {
    setRounds(prev => {
      const newRounds = [...prev];
      // Update Round 1 with preset data
      newRounds[0] = newRounds[0].map((team, idx) => ({
        ...team,
        name: PRESET_TEAMS[idx]?.name || team.name,
        // Convert displayed power (in billions) to internal format
        power: PRESET_TEAMS[idx]?.power || 0,
        gifts: PRESET_TEAMS[idx]?.gifts || 0
      }));
      
      // Reset rounds 2-4 to empty arrays
      newRounds[1] = [];
      newRounds[2] = [];
      newRounds[3] = [];
      
      return newRounds;
    });
    
    // Reset winners
    setWinners([{}, {}, {}, {}]);
    // Reset selected team
    setSelectedTeamId(null);
  };

  const automateRounds = () => {
    const newWinners = [{}, {}, {}, {}];
    const newRounds = [...rounds];
    
    // Process each round
    for (let roundIndex = 0; roundIndex < TOTAL_ROUNDS; roundIndex++) {
      const ranking = newRounds[roundIndex];
      if (ranking.length === 0) continue;
      
      // If this round is locked, preserve both the current order AND existing winners
      if (lockedRounds[roundIndex]) {
        // Preserve existing winners for this locked round
        newWinners[roundIndex] = { ...winners[roundIndex] };
        
        // Still need to calculate next round based on preserved winners
        if (roundIndex < 3) {
          const duels = createDuels(ranking);
          // Calculate next round based on preserved winners
          if (roundIndex >= 1) {
            const previousRoundRanking = newRounds[roundIndex];
            const combinedRanking = calculateNextRankingWithSorting(
              duels,
              newWinners[roundIndex],
              previousRoundRanking,
              newRounds,
              newWinners,
              roundIndex,
              false // keepNulls
            );
            newRounds[roundIndex + 1] = combinedRanking;
          } else {
            const combinedRanking = calculateNextRanking(duels, newWinners[roundIndex]);
            newRounds[roundIndex + 1] = combinedRanking;
          }
        }
        continue; // Skip to next round
      }
      
      // For unlocked rounds, proceed with normal automation
      const duels = createDuels(ranking);
      
      // Determine winners for each duel
      duels.forEach((duel, duelIndex) => {
        const [team1, team2] = duel;
        const winnerId = determineWinner(team1, team2);
        newWinners[roundIndex][duelIndex] = winnerId;
      });
      
      // Calculate next round based on winners
      if (roundIndex < 3) {
        // For rounds 2-4 (roundIndex 1, 2, 3), use sorting logic
        if (roundIndex >= 1) {
          const previousRoundRanking = newRounds[roundIndex];
          const combinedRanking = calculateNextRankingWithSorting(
            duels,
            newWinners[roundIndex],
            previousRoundRanking,
            newRounds,
            newWinners,
            roundIndex,
            false // keepNulls
          );
          newRounds[roundIndex + 1] = combinedRanking;
        } else {
          // For round 1, use the old method (no sorting needed)
          const combinedRanking = calculateNextRanking(duels, newWinners[roundIndex]);
          newRounds[roundIndex + 1] = combinedRanking;
        }
      }
    }
    
    setWinners(newWinners);
    setRounds(newRounds);
  };

  const clearAll = () => {
    if (window.confirm('Are you sure you want to reset the game status?')) {
      const currentTeams = rounds[0];
      setRounds([currentTeams, [], [], []]);
      setWinners([{}, {}, {}, {}]);
      setSelectedTeamId(null);
      setLockedRounds([false, false, false, false]); // Reset to default: all rounds unlocked
    }
  };

  const handleShare = async () => {
    try {
      // Serialize current game state
      const base64State = serializeGameState(rounds, winners, selectedTeamId, lockedRounds);
      
      // Generate share URL
      const shareUrl = `${window.location.origin}${window.location.pathname}?state=${base64State}`;
      
      // Check if Web Share API is available (mobile)
      // Note: navigator.share requires HTTPS (or localhost) and is available on mobile browsers
      if (typeof navigator !== 'undefined' && 'share' in navigator && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: 'Alliance Duel League - Game State',
            text: 'Check out this game state!',
            url: shareUrl
          });
          // Share successful, don't fall through to clipboard
          return;
        } catch (shareError) {
          // User cancelled - don't show error, just return silently
          if (shareError.name === 'AbortError') {
            return;
          }
          // Other errors - log and fall through to clipboard as backup
          console.error('Share failed, falling back to clipboard:', shareError);
          // Continue to clipboard fallback
        }
      }
      
      // Fallback to clipboard copy (desktop or when Web Share API not available)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        // Show feedback (simple alert for now, could be enhanced with toast)
        alert('Game state URL copied to clipboard!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Game state URL copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing game state:', error);
      alert('Failed to share game state. Please try again.');
    }
  };

  const renderHiddenTableForCapture = () => {
    const finalRanking = calculateFinalRanking();
    
    return (
      <div className="max-h-[0px] overflow-hidden">
        <div 
          ref={hiddenTableRef}
          style={{
            display: 'flex',
            gap: '16px',
            padding: '16px',
            backgroundColor: '#0f172a',
            minWidth: 'max-content'
          }}
        >
        {/* Render all rounds */}
        {rounds.map((ranking, roundIndex) => {
          if (ranking.length === 0) return null;
          
          const duels = createDuels(ranking, roundIndex > 0);
          
          return (
            <div
              key={roundIndex}
              style={{
                width: '400px',
                minHeight: '800px',
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: roundIndex === 0 
                  ? 'linear-gradient(to bottom right, rgba(139, 92, 246, 0.4), rgba(168, 85, 247, 0.4))'
                  : roundIndex === 1
                  ? 'linear-gradient(to bottom right, rgba(59, 130, 246, 0.4), rgba(6, 182, 212, 0.4))'
                  : roundIndex === 2
                  ? 'linear-gradient(to bottom right, rgba(16, 185, 129, 0.4), rgba(20, 184, 166, 0.4))'
                  : 'linear-gradient(to bottom right, rgba(99, 102, 241, 0.4), rgba(139, 92, 246, 0.4))',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'linear-gradient(to bottom right, #8b5cf6, #9333ea)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}>
                  {roundIndex + 1}
                </div>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>
                  Round {roundIndex + 1}
                </h2>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {duels.map((duel, duelIndex) => {
                  const currentRank = duelIndex * 2 + 1;
                  const winnerId = winners[roundIndex][duelIndex];
                  
                  // Check if the selected team is in this duel
                  const hasSelectedTeam = selectedTeamId !== null && (
                    (duel[0] && duel[0].id === selectedTeamId) || 
                    (duel[1] && duel[1].id === selectedTeamId)
                  );
                  
                  if (!duel[0] && !duel[1]) return null;
                  
                  return (
                    <div key={duelIndex} style={{ position: 'relative' }}>
                      <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '8px',
                        padding: '8px',
                        border: hasSelectedTeam
                          ? '1px solid rgba(251, 146, 60, 0.7)'
                          : '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: hasSelectedTeam
                          ? '0 0 20px rgba(251, 146, 60, 0.6), 0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        outline: hasSelectedTeam ? '2px solid rgba(251, 146, 60, 0.5)' : 'none',
                        outlineOffset: hasSelectedTeam ? '2px' : '0'
                      }}>
                        <div style={{ position: 'relative' }}>
                          {duel.map((team, idx) => {
                            if (!team) return null;
                            
                            const rank = currentRank + idx;
                            const isWinner = winnerId === team.id;
                            const isLoser = winnerId && winnerId !== team.id;
                            
                            return (
                              <div
                                key={team.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px',
                                  borderRadius: idx === 0 ? '8px 8px 0 0' : '0 0 8px 8px',
                                  background: isWinner
                                    ? 'linear-gradient(to right, rgba(16, 185, 129, 0.9), rgba(20, 184, 166, 0.9))'
                                    : isLoser
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(255, 255, 255, 0.1)',
                                  opacity: isLoser ? 0.5 : 1
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '24px',
                                  height: '24px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  background: isWinner ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                  color: isWinner ? 'white' : 'rgba(255, 255, 255, 0.6)'
                                }}>
                                  #{rank}
                                </div>
                                
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                  <span style={{
                                    flex: 1,
                                    color: 'white',
                                    fontWeight: '500',
                                    fontSize: '12px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {team.name}
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                    <span style={{ color: '#fbbf24', fontSize: '12px' }}>⚡</span>
                                    <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>G</span>
                                    <span style={{ color: 'white', fontWeight: '500', fontSize: '12px', width: '48px' }}>
                                      {formatPowerDisplay(team.power || 0)}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                    <span style={{ fontSize: '12px' }}>🎁</span>
                                    <span style={{ color: 'white', fontWeight: '500', fontSize: '12px', width: '32px' }}>
                                      {team.gifts || 0}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        
        {/* Render final ranking if available */}
        {finalRanking && finalRanking.length > 0 && (
          <div
            style={{
              width: '400px',
              height: '906px',
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              background: 'linear-gradient(to bottom, rgba(16, 185, 129, 0.6), rgba(16, 185, 129, 0.4))',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>🏆</span>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>
                Final Ranking
              </h2>
            </div>
            
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
              {finalRanking.map((team, index) => {
                const rank = index + 1;
                const isFirst = rank === 1;
                const isSelected = selectedTeamId === team.id;
                
                return (
                  <div
                    key={team.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      borderRadius: '8px',
                      background: isFirst
                        ? 'linear-gradient(to right, rgba(234, 179, 8, 0.3), rgba(251, 191, 36, 0.3))'
                        : 'rgba(0, 0, 0, 0.3)',
                      border: isFirst
                        ? '1px solid rgba(251, 191, 36, 0.5)'
                        : isSelected
                        ? '1px solid rgba(251, 146, 60, 0.7)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: isFirst 
                        ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' 
                        : isSelected
                        ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 0 20px rgba(251, 146, 60, 0.4)'
                        : 'none',
                      outline: isSelected ? '2px solid rgba(251, 146, 60, 0.7)' : 'none',
                      outlineOffset: isSelected ? '2px' : '0'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '28px',
                      height: '24px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      background: isFirst ? 'rgba(251, 191, 36, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                      color: isFirst ? '#fef3c7' : 'rgba(255, 255, 255, 0.6)'
                    }}>
                      #{rank}
                    </div>
                    
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minWidth: 0 }}>
                      <span style={{
                        color: 'white',
                        fontWeight: '500',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {team.name}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {team.history && team.history.length > 0 ? (
                          team.history.map((result, idx) => (
                            <span
                              key={idx}
                              style={{
                                width: '34px',
                                padding: '2px 0',
                                borderRadius: '4px',
                                fontSize: '9px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                background: result === 'WIN'
                                  ? 'rgba(16, 185, 129, 0.3)'
                                  : 'rgba(239, 68, 68, 0.3)',
                                color: result === 'WIN'
                                  ? '#6ee7b7'
                                  : '#fca5a5',
                                border: result === 'WIN'
                                  ? '1px solid rgba(16, 185, 129, 0.5)'
                                  : '1px solid rgba(239, 68, 68, 0.5)'
                              }}
                            >
                              {result}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '9px' }}>No matches</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>
    );
  };

  const handleCaptureImage = async () => {
    try {
      if (!hiddenTableRef.current) {
        console.error('Hidden table ref not available');
        return;
      }
      
      const element = hiddenTableRef.current;
      
      // Check if element has content and rounds have data
      const hasRounds = rounds.some(round => round && round.length > 0);
      if (!hasRounds) {
        console.error('No rounds with data');
        alert('No content to capture. Please ensure at least one round has teams.');
        return;
      }
      
      if (!element.children || element.children.length === 0) {
        console.error('Hidden table has no content', { 
          elementExists: !!element,
          innerHTML: element.innerHTML?.substring(0, 100),
          children: element.children?.length
        });
        alert('No content to capture. Please ensure rounds are populated.');
        return;
      }
      
      console.log('Capturing image with', element.children.length, 'round containers');
      
      // Element is already rendered (just hidden by parent container)
      // No need to modify styles - html-to-image can capture hidden elements
      
      // Wait for fonts to be ready
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      
      // Wait a bit for rendering
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get dimensions
      const width = element.scrollWidth || element.offsetWidth;
      const height = element.scrollHeight || element.offsetHeight;
      
      console.log('Element dimensions:', { 
        width, 
        height, 
        children: element.children.length,
        hasContent: element.innerHTML.length > 0,
        computedStyles: {
          display: window.getComputedStyle(element).display,
          visibility: window.getComputedStyle(element).visibility,
          opacity: window.getComputedStyle(element).opacity
        }
      });
      
      if (width === 0 || height === 0) {
        console.error('Element has zero dimensions', { 
          width, 
          height, 
          children: element.children.length,
          computedWidth: window.getComputedStyle(element).width,
          computedHeight: window.getComputedStyle(element).height,
          innerHTML: element.innerHTML.substring(0, 200)
        });
        alert('Unable to capture image. The content may not be fully loaded. Please ensure rounds have content.');
        return;
      }
      
      // Try toCanvas first (sometimes more reliable), then convert to data URL
      let dataUrl;
      try {
        const canvas = await htmlToImage.toCanvas(element, {
          backgroundColor: '#0f172a',
          quality: 1.0,
          pixelRatio: 2,
          cacheBust: true,
          useCORS: true,
          allowTaint: false,
          width: width,
          height: height
        });
        dataUrl = canvas.toDataURL('image/png');
      } catch (canvasError) {
        console.warn('Canvas capture failed, trying PNG directly:', canvasError);
        // Fallback to toPng
        dataUrl = await htmlToImage.toPng(element, {
          backgroundColor: '#0f172a',
          quality: 1.0,
          pixelRatio: 2,
          cacheBust: true,
          useCORS: true,
          allowTaint: false,
          width: width,
          height: height
        });
      }
      
      setCapturedImageUrl(dataUrl);
      setShowImageModal(true);
    } catch (error) {
      console.error('Error capturing image:', error);
      alert('Failed to capture image. Please try again.');
    }
  };

  const handleDownloadImage = () => {
    if (!capturedImageUrl) return;
    
    const link = document.createElement('a');
    link.download = 'alliance-duel-league.png';
    link.href = capturedImageUrl;
    link.click();
  };

  const handleShareImage = async () => {
    if (!capturedImageUrl) return;
    
    try {
      // Convert data URL to blob
      const response = await fetch(capturedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'alliance-duel-league.png', { type: 'image/png' });
      
      // Check if Web Share API supports files
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: 'Alliance Duel League - Tournament Results',
            text: 'Check out these tournament results!',
            files: [file]
          });
          return;
        } catch (shareError) {
          if (shareError.name === 'AbortError') {
            return;
          }
          console.error('Share failed, falling back to download:', shareError);
        }
      }
      
      // Fallback to download
      handleDownloadImage();
    } catch (error) {
      console.error('Error sharing image:', error);
      // Fallback to download
      handleDownloadImage();
    }
  };

  const calculateFinalRanking = () => {
    // Check if round 4 is complete
    const round4 = rounds[3];
    const round4Winners = winners[3];
    
    if (!round4 || round4.length === 0) return null;
    
    // Check if all duels in round 4 have winners
    const duels = createDuels(round4);
    const allWinnersSelected = duels.every((_, idx) => round4Winners[idx] !== undefined);
    
    if (!allWinnersSelected) return null;
    
    // Initialize team statistics
    const teamStats = new Map();
    
    // Initialize all teams from round 0
    rounds[0].forEach(team => {
      if (team) {
        teamStats.set(team.id, {
          team,
          wins: 0,
          losses: 0,
          history: [], // Track win/loss history in chronological order
          winRounds: [] // Track which rounds each win occurred in
        });
      }
    });
    
    // Calculate wins and losses for each round in chronological order
    for (let roundIndex = 0; roundIndex < TOTAL_ROUNDS; roundIndex++) {
      const roundRanking = rounds[roundIndex];
      if (roundRanking.length === 0) continue;
      
      const roundDuels = createDuels(roundRanking);
      const roundWinners = winners[roundIndex];
      
      roundDuels.forEach((duel, duelIndex) => {
        const winnerId = roundWinners[duelIndex];
        if (winnerId !== undefined && duel[0] && duel[1]) {
          const winner = teamStats.get(winnerId);
          const loser = duel.find(t => t && t.id !== winnerId);
          
          if (winner) {
            winner.wins++;
            winner.history.push('WIN');
            winner.winRounds.push(roundIndex); // Track which round this win occurred in
          }
          
          if (loser) {
            const loserStats = teamStats.get(loser.id);
            if (loserStats) {
              loserStats.losses++;
              loserStats.history.push('LOSE');
            }
          }
        }
      });
    }
    
    // Collect all teams from round 4
    const allTeams = [];
    const round4Duels = createDuels(round4);
    
    round4Duels.forEach((duel, duelIndex) => {
      const winnerId = round4Winners[duelIndex];
      if (winnerId !== undefined) {
        const winner = duel.find(t => t && t.id === winnerId);
        const loser = duel.find(t => t && t.id !== winnerId);
        
        if (winner) {
          const stats = teamStats.get(winner.id);
          if (stats) {
            allTeams.push({
              ...stats.team,
              wins: stats.wins,
              losses: stats.losses,
              history: stats.history,
              winRounds: stats.winRounds
            });
          }
        }
        if (loser) {
          const stats = teamStats.get(loser.id);
          if (stats) {
            allTeams.push({
              ...stats.team,
              wins: stats.wins,
              losses: stats.losses,
              history: stats.history,
              winRounds: stats.winRounds
            });
          }
        }
      }
    });
    
    // Sort according to rules:
    // 1. Higher number of victories leads to higher ranking
    // 2. If victory counts are equal, earlier wins rank higher
    // 3. When wins and order of wins are the same, ranking is random
    allTeams.sort((a, b) => {
      // Rule 1: Compare by number of wins (descending)
      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }
      
      // Rule 2: If wins are equal, compare by earliest win round
      // Teams with wins in earlier rounds rank higher
      if (a.winRounds.length > 0 && b.winRounds.length > 0) {
        const aEarliestWin = Math.min(...a.winRounds);
        const bEarliestWin = Math.min(...b.winRounds);
        if (aEarliestWin !== bEarliestWin) {
          return aEarliestWin - bEarliestWin; // Earlier round = higher rank
        }
        
        // If earliest wins are in the same round, compare all win rounds in order
        for (let i = 0; i < Math.min(a.winRounds.length, b.winRounds.length); i++) {
          if (a.winRounds[i] !== b.winRounds[i]) {
            return a.winRounds[i] - b.winRounds[i]; // Earlier round = higher rank
          }
        }
      }
      
      // Rule 3: If wins and order of wins are the same, random order
      // Use team ID for deterministic but arbitrary ordering
      // This provides consistent results while appearing random
      return a.id - b.id;
    });
    
    return allTeams;
  };

  const renderFinalRanking = () => {
    const finalRanking = calculateFinalRanking();
    
    if (!finalRanking || finalRanking.length === 0) return null;
    
    return (
      <div className="w-full md:w-auto">
        <div className="bg-gradient-to-b from-emerald-500/40 to-emerald-500/20 backdrop-blur-sm rounded-xl border border-amber-500/30 p-3 shadow-2xl h-[906px] md:w-[400px] flex flex-col relative">
          <div className="flex items-center justify-center gap-2 mb-3 relative">
            <Trophy size={24} className="text-yellow-400 drop-shadow-lg" />
            <h2 className="text-lg font-bold text-white">
              Final Ranking
            </h2>
            <div className="absolute top-0 right-0 flex items-center gap-2">
              <button
                onClick={handleCaptureImage}
                className="p-1.5 rounded-lg bg-violet-500/80 hover:bg-violet-600/90 text-white hover:text-white transition-all duration-200 flex items-center justify-center"
                aria-label="Capture image"
              >
                <Camera size={18} />
              </button>
              <button
                onClick={handleShare}
                className="p-1.5 rounded-lg bg-violet-500/80 hover:bg-violet-600/90 text-white hover:text-white transition-all duration-200 flex items-center justify-center"
                aria-label="Share game state"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
          
          <div className="flex-1 space-y-2 overflow-y-auto">
            {finalRanking.map((team, index) => {
              const rank = index + 1;
              const isFirst = rank === 1;
              const isSelected = selectedTeamId === team.id;
              
              return (
                <div
                  key={team.id}
                  onClick={() => {
                    // Toggle selection: if already selected, deselect; otherwise select
                    setSelectedTeamId(isSelected ? null : team.id);
                  }}
                  className={`relative flex items-center gap-2 p-2 rounded-lg transition-all duration-300 cursor-pointer hover:bg-white/10 ${
                    isFirst
                      ? 'bg-gradient-to-r from-yellow-500/30 to-amber-500/30 border border-yellow-400/50 shadow-lg'
                      : 'bg-black/30 backdrop-blur-md border border-white/10'
                  } ${
                    isSelected ? 'ring-2 ring-orange-500/70 shadow-lg shadow-orange-500/40' : ''
                  }`}
                >
                  <div className={`flex items-center justify-center min-w-[28px] h-6 rounded text-[10px] font-bold ${
                    isFirst
                      ? 'bg-yellow-400/30 text-yellow-200'
                      : 'bg-white/10 text-white/60'
                  }`}>
                    #{rank}
                  </div>
                  
                  <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                    <span className="text-white font-medium text-xs truncate">
                      {team.name}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {team.history && team.history.length > 0 ? (
                        team.history.map((result, idx) => (
                          <span
                            key={idx}
                            className={`w-[34px] py-0.5 rounded text-[9px] font-semibold whitespace-nowrap text-center ${
                              result === 'WIN'
                                ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/50'
                                : 'bg-red-500/30 text-red-300 border border-red-400/50'
                            }`}
                          >
                            {result}
                          </span>
                        ))
                      ) : (
                        <span className="text-white/60 text-[9px]">No matches</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get score history for a team up to a given round
  const getTeamScoreHistory = (teamId, upToRoundIndex) => {
    const history = [];
    for (let roundIdx = 0; roundIdx < upToRoundIndex; roundIdx++) {
      const roundRanking = rounds[roundIdx];
      if (!roundRanking || roundRanking.length === 0) continue;
      
      const roundDuels = createDuels(roundRanking);
      const roundWinners = winners[roundIdx] || {};
      
      // Find the duel this team participated in
      for (let duelIdx = 0; duelIdx < roundDuels.length; duelIdx++) {
        const duel = roundDuels[duelIdx];
        const winnerId = roundWinners[duelIdx];
        
        if (winnerId !== undefined && duel[0] && duel[1]) {
          if (duel[0].id === teamId || duel[1].id === teamId) {
            const isWinner = winnerId === teamId;
            history.push(isWinner ? 'WIN' : 'LOSE');
            break; // Found the team's duel for this round, move to next round
          }
        }
      }
    }
    return history;
  };

  const toggleRoundLock = (roundIndex) => {
    // Check if unlocking and there are subsequent locked rounds
    if (lockedRounds[roundIndex]) {
      // Check if any subsequent rounds are locked
      const hasSubsequentLocks = lockedRounds.slice(roundIndex + 1).some(locked => locked);
      if (hasSubsequentLocks) {
        const subsequentRounds = [];
        for (let i = roundIndex + 1; i < TOTAL_ROUNDS; i++) {
          if (lockedRounds[i]) {
            subsequentRounds.push(i + 1);
          }
        }
        const message = `Unlocking Round ${roundIndex + 1} will also unlock Round${subsequentRounds.length > 1 ? 's' : ''} ${subsequentRounds.join(', ')}. Continue?`;
        if (!window.confirm(message)) {
          return;
        }
        // Unlock this round and all subsequent rounds
        const newLockedRounds = [...lockedRounds];
        for (let i = roundIndex; i < TOTAL_ROUNDS; i++) {
          newLockedRounds[i] = false;
        }
        setLockedRounds(newLockedRounds);
        return;
      }
    }
    
    // Toggle the lock state for this round
    const newLockedRounds = [...lockedRounds];
    newLockedRounds[roundIndex] = !newLockedRounds[roundIndex];
    setLockedRounds(newLockedRounds);
  };

  const isLockButtonEnabled = (roundIndex) => {
    if (roundIndex === 0) return true; // Round 1 is always enabled
    // For other rounds, enabled only if previous round is locked
    return lockedRounds[roundIndex - 1] === true;
  };

  const renderRound = (roundIndex) => {
    const ranking = rounds[roundIndex];
    if (ranking.length === 0) return null;

    // Allow partial duels for rounds after the first one (to maintain layout)
    const duels = createDuels(ranking, roundIndex > 0);
    const isLocked = lockedRounds[roundIndex];
    const lockButtonEnabled = isLockButtonEnabled(roundIndex);

    return (
      <div className="w-full md:w-auto">
        <div className={`bg-gradient-to-br ${ROUND_COLORS[roundIndex]} backdrop-blur-sm rounded-xl border p-3 shadow-2xl min-h-[800px] md:w-[400px] overflow-visible`}>
          <div className="flex items-center justify-center gap-2 mb-3 relative overflow-visible">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-lg">
              {roundIndex + 1}
            </div>
            <h2 className="text-lg font-bold text-white">
              Round {roundIndex + 1}
            </h2>
            <div className="absolute right-0 flex items-center gap-2 overflow-visible">
              <button
                onClick={() => toggleRoundLock(roundIndex)}
                disabled={!lockButtonEnabled}
                className={`p-1.5 rounded-lg transition-all duration-200 flex items-center justify-center ${
                  lockButtonEnabled
                    ? isLocked
                      ? 'bg-red-500/80 hover:bg-red-600/90 text-white'
                      : 'bg-green-500/80 hover:bg-green-600/90 text-white'
                    : 'bg-gray-500/50 text-gray-400 cursor-not-allowed'
                }`}
                aria-label={isLocked ? 'Unlock round' : 'Lock round'}
                title={lockButtonEnabled ? (isLocked ? 'Unlock round' : 'Lock round') : 'Previous round must be locked first'}
              >
                {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
              </button>
              <div className="relative group overflow-visible">
                <Info 
                  size={16} 
                  className="text-violet-300/60 hover:text-violet-300 cursor-help transition-colors"
                />
                <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-slate-900/95 text-white text-xs rounded-lg shadow-xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-normal z-[100] max-w-[min(calc(100vw-2rem),20rem)]">
                  {isLocked 
                    ? 'Locked rounds preserve team order and winner selections when using Auto.'
                    : 'Lock this round to preserve team order and winner selections when using Auto.'}
                  <div className="absolute bottom-full right-4 mb-0 border-4 border-transparent border-b-slate-900/95"></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            {duels.map((duel, duelIndex) => {
              const currentRank = duelIndex * 2 + 1;
              const winnerId = winners[roundIndex][duelIndex];
              
              // Check if the selected team is in this duel
              const hasSelectedTeam = selectedTeamId !== null && (
                (duel[0] && duel[0].id === selectedTeamId) || 
                (duel[1] && duel[1].id === selectedTeamId)
              );
              
              // Skip rendering if both teams are null
              if (!duel[0] && !duel[1]) {
                return null;
              }
              
              return (
                <div key={duelIndex} className="relative">
                  <div className={`bg-black/30 backdrop-blur-md rounded-lg p-2 border shadow-lg transition-all duration-300 ${
                    hasSelectedTeam 
                      ? 'border-orange-500/70 shadow-[0_0_20px_rgba(251,146,60,0.6)] ring-2 ring-orange-500/50' 
                      : 'border-white/10'
                  }`}>
                    <div className="relative">
                      {duel.map((team, idx) => {
                        const rank = currentRank + idx;
                        
                        // Render placeholder if team is null
                        if (!team) {
                          return (
                            <div
                              key={`null-${idx}`}
                              className={`relative flex items-center gap-2 p-2 rounded transition-all duration-300 bg-white/5 opacity-30 ${
                                idx === 0 ? 'rounded-b-none' : 'rounded-t-none'
                              }`}
                            >
                              <div className="flex items-center justify-center min-w-[24px] h-6 rounded text-[10px] font-bold bg-white/10 text-white/40">
                                #{rank}
                              </div>
                              <div className="flex-1 flex items-center gap-2 min-w-0">
                                <span className="flex-1 text-white/40 font-medium text-xs italic">Waiting...</span>
                              </div>
                            </div>
                          );
                        }
                        
                        const isWinner = winnerId === team.id;
                        const isLoser = winnerId && winnerId !== team.id;
                        
                        return (
                          <div
                            key={team.id}
                            className={`group relative flex items-center gap-2 p-2 rounded transition-all duration-300 ${
                              editingTeamId === team.id ? 'cursor-text' : 'cursor-pointer'
                            } ${
                              idx === 0 ? 'rounded-b-none' : 'rounded-t-none'
                            } ${
                              isWinner ? 'bg-gradient-to-r from-emerald-500/90 to-teal-500/90 shadow-md shadow-emerald-500/20' : 
                              isLoser ? 'bg-white/5 opacity-50' : 
                              'bg-white/10 hover:bg-white/20'
                            }`}
                            onClick={() => {
                              if (editingTeamId !== team.id) {
                                selectWinner(roundIndex, duelIndex, team.id);
                              }
                            }}
                          >
                            <div className={`flex items-center justify-center min-w-[24px] h-6 rounded text-[10px] font-bold ${
                              isWinner ? 'bg-white/20 text-white' : 'bg-white/10 text-white/60'
                            }`}>
                              #{rank}
                            </div>
                            
                            {roundIndex === 0 ? (
                              <>
                                <div className="flex-1 flex items-center gap-2 min-w-0">
                                  <input
                                    type="text"
                                    value={team.name}
                                    onChange={(e) => updateTeamName(team.id, e.target.value)}
                                    onBlur={(e) => {
                                      if (!e.relatedTarget || !e.currentTarget.closest('.group').contains(e.relatedTarget)) {
                                        setEditingTeamId(null);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        setEditingTeamId(null);
                                        e.target.blur();
                                      }
                                    }}
                                    onFocus={(e) => {
                                      if (editingTeamId === team.id) {
                                        e.target.select();
                                      }
                                    }}
                                    onClick={(e) => {
                                      if (editingTeamId === team.id) {
                                        e.stopPropagation();
                                      }
                                    }}
                                    readOnly={editingTeamId !== team.id}
                                    maxLength={MAX_TEAM_NAME_LENGTH}
                                    className={`flex-1 bg-transparent text-white font-medium text-xs outline-none placeholder-white/40 min-w-0 ${
                                      editingTeamId === team.id ? 'cursor-text pointer-events-auto' : 'cursor-pointer pointer-events-none'
                                    }`}
                                    placeholder="Team Name"
                                  />
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-yellow-400 text-xs">⚡</span>
                                    <span className="text-white/60 text-xs">G</span>
                                    <input
                                      type="number"
                                      value={editingTeamId === team.id && editingPowerValue !== '' 
                                        ? editingPowerValue 
                                        : formatPowerDisplay(team.power || 0)}
                                      onChange={(e) => updateTeamPower(team.id, e.target.value, true)}
                                      onBlur={(e) => {
                                        // Commit the power value
                                        if (editingPowerValue !== '') {
                                          updateTeamPower(team.id, parseFloat(editingPowerValue), false);
                                        }
                                        if (!e.relatedTarget || !e.currentTarget.closest('.group').contains(e.relatedTarget)) {
                                          setEditingTeamId(null);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          if (editingPowerValue !== '') {
                                            updateTeamPower(team.id, parseFloat(editingPowerValue), false);
                                          }
                                          setEditingTeamId(null);
                                          e.target.blur();
                                        }
                                      }}
                                      onFocus={(e) => {
                                        if (editingTeamId === team.id) {
                                          setEditingPowerValue(formatPowerDisplay(team.power || 0));
                                          setTimeout(() => e.target.select(), 0);
                                        }
                                      }}
                                      onClick={(e) => {
                                        if (editingTeamId === team.id) {
                                          e.stopPropagation();
                                        }
                                      }}
                                      readOnly={editingTeamId !== team.id}
                                      min="0"
                                      step="0.001"
                                      className={`w-12 bg-transparent text-white font-medium text-xs outline-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                        editingTeamId === team.id ? 'cursor-text pointer-events-auto' : 'cursor-pointer pointer-events-none'
                                      }`}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-xs">🎁</span>
                                    <input
                                      type="number"
                                      value={team.gifts || 0}
                                      onChange={(e) => updateTeamGifts(team.id, e.target.value)}
                                      onBlur={(e) => {
                                        if (!e.relatedTarget || !e.currentTarget.closest('.group').contains(e.relatedTarget)) {
                                          setEditingTeamId(null);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          setEditingTeamId(null);
                                          e.target.blur();
                                        }
                                      }}
                                      onFocus={(e) => {
                                        if (editingTeamId === team.id) {
                                          e.target.select();
                                        }
                                      }}
                                      onClick={(e) => {
                                        if (editingTeamId === team.id) {
                                          e.stopPropagation();
                                        }
                                      }}
                                      readOnly={editingTeamId !== team.id}
                                      min="0"
                                      max="99"
                                      className={`w-8 bg-transparent text-white font-medium text-xs outline-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                        editingTeamId === team.id ? 'cursor-text pointer-events-auto' : 'cursor-pointer pointer-events-none'
                                      }`}
                                    />
                                  </div>
                                </div>
                                <Pencil 
                                  size={12} 
                                  className="text-white/40 hover:text-white/80 transition-colors flex-shrink-0 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingTeamId(team.id);
                                    setTimeout(() => {
                                      const input = e.target.closest('.group').querySelector('input[type="text"]');
                                      if (input) {
                                        input.focus();
                                        input.select();
                                      }
                                    }, 0);
                                  }}
                                />
                              </>
                            ) : (
                              <div className="flex-1 flex items-center gap-2 min-w-0">
                                <span className="flex-1 text-white font-medium text-xs truncate">{team.name}</span>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className="text-yellow-400 text-xs">⚡</span>
                                  <span className="text-white/60 text-xs">G</span>
                                  <span className="text-white font-medium text-xs w-12">{formatPowerDisplay(team.power || 0)}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className="text-xs">🎁</span>
                                  <span className="text-white font-medium text-xs w-8">{team.gifts || 0}</span>
                                  {roundIndex >= 1 && (
                                    <div className="flex flex-col gap-1 ml-1.5">
                                      {getTeamScoreHistory(team.id, roundIndex).map((result, idx) => (
                                        <div
                                          key={idx}
                                          className={`w-[5px] h-[5px] rounded-full ${
                                            result === 'WIN' 
                                              ? 'bg-emerald-400 border-2 border-emerald-300 shadow-[0_0_2px_1px_rgba(16,185,129,0.5)]' 
                                              : 'bg-red-400 border border-red-300 shadow-[0_0_2px_1px_rgba(239,68,68,0.5)]'
                                          }`}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* VS Icon at the left middle between teams */}
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 pointer-events-none">
                        <div className="relative w-6 h-6 flex items-center justify-center">
                          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-950 rounded-full border-2 border-red-500/60 shadow-lg"></div>
                          <span className="relative z-10 text-red-400 font-black text-[10px] tracking-tighter">VS</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-3 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 mb-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 drop-shadow-lg">
              Alliance Duel League
            </h1>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <button
                onClick={loadPresetNames}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-bold text-sm rounded-lg transition-all duration-300 shadow-lg shadow-violet-500/30 hover:shadow-xl hover:shadow-violet-500/50 hover:scale-105"
              >
                {CURRENT_PRESET_LABEL}
              </button>
              
              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold text-sm rounded-lg transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/50 hover:scale-105"
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={automateRounds}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-sm rounded-lg transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/50 hover:scale-105"
              >
                ⚡ Auto
              </button>
              <div className="relative group">
                <Info 
                  size={16} 
                  className="text-violet-300/60 hover:text-violet-300 cursor-help transition-colors"
                />
                <div className="absolute top-full left-1/2 -translate-x-1/2 md:bottom-full md:top-auto mt-2 md:mb-2 px-3 py-2 bg-slate-900/95 text-white text-xs rounded-lg shadow-xl border border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none whitespace-normal md:whitespace-nowrap z-50 max-w-[min(calc(100vw-2rem),20rem)] md:w-auto md:max-w-none">
                  Power-based selection. If within 10%, uses gifts. If gifts ±2, random choice.
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0 md:top-full md:bottom-auto md:-mt-1 border-4 border-transparent border-b-slate-900/95 md:border-b-transparent md:border-t-slate-900/95"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div 
          ref={carouselRef}
          className="flex gap-4 overflow-x-auto pb-4 snap-x-smooth scrollbar-hide"
        >
          {rounds.map((_, idx) => (
            <div key={idx} className="snap-center w-full md:w-auto flex-shrink-0">
              {renderRound(idx)}
            </div>
          ))}
          {calculateFinalRanking() && (
            <div className="snap-center w-full md:w-auto flex-shrink-0">
              {renderFinalRanking()}
            </div>
          )}
        </div>

        {/* Horizontal scroll indicator */}
        <div className="relative w-full h-2 bg-white/10 rounded-full overflow-hidden mb-4">
          <div 
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-150"
            style={{ width: `${Math.max(10, scrollProgress)}%` }}
          />
        </div>
        
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          @keyframes pulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.5; }
          }
          .animate-pulse {
            animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          .delay-1000 {
            animation-delay: 1s;
          }
        `}</style>
      </div>
      
      {/* Hidden table for image capture */}
      {renderHiddenTableForCapture()}
      
      {/* Image Modal */}
      {showImageModal && capturedImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close button - top right */}
            <button
              onClick={() => {
                setShowImageModal(false);
                setCapturedImageUrl(null);
              }}
              className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-200 flex items-center justify-center z-10"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            
            {/* Image container */}
            <div className="max-w-full max-h-full overflow-auto">
              <img 
                src={capturedImageUrl} 
                alt="Tournament results" 
                className="max-w-full h-auto"
              />
            </div>
            
            {/* Bottom controls */}
            <div className="absolute bottom-4 right-4 flex items-center gap-2">
              {/* Download button - to the left of share button */}
              <button
                onClick={handleDownloadImage}
                className="flex items-center gap-2 px-4 py-2 bg-violet-500/80 hover:bg-violet-600/90 text-white font-medium rounded-lg transition-all duration-200 shadow-lg"
              >
                <Download size={18} />
                <span>Download</span>
              </button>
              
              {/* Share button - to the right of download button */}
              <button
                onClick={handleShareImage}
                className="p-2 rounded-lg bg-violet-500/80 hover:bg-violet-600/90 text-white transition-all duration-200 flex items-center justify-center shadow-lg"
                aria-label="Share image"
              >
                <Share2 size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamRankingGame;
